import { Select } from "antd";

import { AppStrings } from "../../utils/constants/AppStrings";
import type {
  ReportingSummaryFilterKey,
  ReportingSummaryFilterOptions,
  ReportingSummaryFiltersValue,
} from "../../queries/sportsbook/reportingSummaryFilterOptions";
import { normalizeReportingSummaryMultiSelectValue } from "./reportingSummaryFiltersUtils";

interface FieldDescriptor {
  key: ReportingSummaryFilterKey;
  label: string;
  testId: string;
}

const FILTER_FIELDS: FieldDescriptor[] = [
  {
    key: "money_type",
    label: AppStrings.REPORTING_FILTERS_MONEY_TYPE,
    testId: "reporting-summary-filter-money-type",
  },
  {
    key: "bet_status",
    label: AppStrings.REPORTING_FILTERS_BET_STATUS,
    testId: "reporting-summary-filter-bet-status",
  },
  {
    key: "bet_type",
    label: AppStrings.SPORTSBOOK_PROFITABILITY_BET_TYPE,
    testId: "reporting-summary-filter-bet-type",
  },
  {
    key: "sport",
    label: AppStrings.REPORTING_FILTERS_SPORT,
    testId: "reporting-summary-filter-sport",
  },
  {
    key: "league",
    label: AppStrings.REPORTING_FILTERS_LEAGUE,
    testId: "reporting-summary-filter-league",
  },
  {
    key: "odds_provider",
    label: AppStrings.REPORTING_FILTERS_ODDS_PROVIDER,
    testId: "reporting-summary-filter-odds-provider",
  },
  {
    key: "state",
    label: AppStrings.REPORTING_FILTERS_STATE,
    testId: "reporting-summary-filter-state",
  },
];

export interface ReportingSummaryFiltersProps {
  options: ReportingSummaryFilterOptions | null;
  draft: ReportingSummaryFiltersValue | null;
  onDraftChange: (key: ReportingSummaryFilterKey, next: string[]) => void;
  onApply: () => void;
  onClear: () => void;
  isDirty: boolean;
  loading?: boolean;
}

function ReportingSummaryFilters({
  options,
  draft,
  onDraftChange,
  onApply,
  onClear,
  isDirty,
  loading = false,
}: ReportingSummaryFiltersProps) {
  return (
    <div
      className="reporting-summary-filters-panel"
      data-testid="reporting-summary-filters-panel"
    >
      <div className="reporting-summary-filters-panel-selectors">
        {FILTER_FIELDS.map((field) => {
          const group = options?.[field.key];
          const value = draft?.[field.key] ?? [];
          const isMultiSelect = group?.multiSelect ?? true;
          const selectValue = isMultiSelect ? value : value[0];
          return (
            <div
              key={field.key}
              className="reporting-summary-filter-field"
              data-testid={field.testId}
            >
              <span className="reporting-summary-filter-field-label">
                {field.label}
              </span>
              <Select
                mode={isMultiSelect ? "multiple" : undefined}
                allowClear
                showSearch
                maxTagCount="responsive"
                loading={loading}
                disabled={loading || !group}
                placeholder={AppStrings.NOT_SELECTED}
                optionFilterProp="label"
                options={group?.options ?? []}
                value={selectValue}
                onChange={(next) => {
                  if (Array.isArray(next)) {
                    onDraftChange(
                      field.key,
                      normalizeReportingSummaryMultiSelectValue(value, next),
                    );
                    return;
                  }
                  onDraftChange(field.key, next ? [next] : []);
                }}
              />
            </div>
          );
        })}
      </div>
      <div className="reporting-summary-filter-actions">
        <button
          type="button"
          className="reporting-summary-filter-clear"
          onClick={onClear}
          data-testid="reporting-summary-filter-clear"
        >
          {AppStrings.CLEAR_FILTERS}
        </button>
        <button
          type="button"
          className={
            isDirty
              ? "reporting-summary-filter-apply reporting-summary-filter-apply--dirty"
              : "reporting-summary-filter-apply"
          }
          onClick={onApply}
          data-testid="reporting-summary-filter-apply"
          disabled={!isDirty || loading}
        >
          {AppStrings.APPLY}
        </button>
      </div>
    </div>
  );
}

export default ReportingSummaryFilters;
